import { useSelector } from "react-redux";
import { FlagApplicationNames, IFeatureFlagWithBVT, IUseBvtFlagsCheckerProps } from "../types";
import { ISubscriptionsClientState } from "@telstra-connect/subscriptions/state";
import { findBvtFlagFromApplicationConfig, isFeatureEnabled } from "../util";
import { ApplicationNames } from "@telstra-connect/utils/data/platform-data";
import useCustomerTypeCidn from "./use-customer-cidn";

/**
 * Custom hook to check if a BVT (Business Value Test) feature flag is enabled for the current user.
 * It retrieves configuration from the Redux store using useSelector and determines
 * if the flag is enabled based on user details and application settings.
 *
 * @param {IUseBvtFlagsCheckerProps} props - The props for the hook, including the BVT flag name and optional cidn.
 * @param {string} props.bvtFlagName - The name of the BVT feature flag to check.
 * @param {string} [props.cidn] - Optional customer ID to use for checking the flag. If not provided, the user's cidn from the Redux store is used.
 * @param {string} [props.appName] - Optional application name for which bvt flag will be searched. Default - Present application
 * @returns {object} An object containing the status of the BVT flag.
 * @returns {boolean} .isBvtFlagEnabled - True if the feature is enabled for the user; otherwise, false.
 */
const useBvtFlagsChecker = ({ bvtFlagName, cidn, appName }: IUseBvtFlagsCheckerProps) => {
    // Retrieve the application configuration from the Redux store.
    const applicationConfig = useSelector(
        (state: ISubscriptionsClientState) =>
            state.subscriptions.applicationConfig.data.applications
    );
    // Retrieve the user's company ID (cidn) based on the user's type.
    const { userCidn } = useCustomerTypeCidn();
    // Determine the application name for which to look up the feature flag.
    const application: FlagApplicationNames = appName ?? ApplicationNames.IncidentManagement;
    // Retrieve the specific BVT feature flag based on the provided flag name
    const bvtFlag: IFeatureFlagWithBVT = findBvtFlagFromApplicationConfig(applicationConfig, application, bvtFlagName);
    // Retrieve the list of test customers from the platform application configuration
    const isTestCustomer: string[] = applicationConfig?.Platform?.isTestCustomer;
    // Determine the selected cidn, defaulting to the user's cidn if not provided.
    const selectedCidn: string = cidn ?? userCidn;
    // Determine if the BVT feature flag is enabled for the current user
    return {
        isBvtFlagEnabled: isFeatureEnabled(bvtFlag, selectedCidn, isTestCustomer)
    }
}

export default useBvtFlagsChecker;
